import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useStores } from '../../../../stores/useStores';
import Button from '../../../buttons/Button';
import { DialogBox } from '../../../Dialog/DialogBox';
import { DialogContent } from '../../../Dialog/DialogContent';

import { getInvalidPromoCodeToMsgMap } from './constants';
import styles from './InvalidPromoDialog.module.scss';
import type { InvalidPromoErrorReason } from './types';

const InvalidPromoDialog = observer(() => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { portalStore, promotionStore } = useStores();
  const { t } = useTranslation();

  const { options } = portalStore;

  const removePcQueryParam = () => {
    if (searchParams.has('pc')) {
      searchParams.delete('pc');
      setSearchParams(searchParams);
    }
  };

  const closeDialog = () => {
    removePcQueryParam();
    promotionStore.closePromotionDialog();
  };

  const onCloseClickHandler = () => {
    promotionStore.sendAnalyticsEvent({ event_action: 'click', click_location: 'close' });
    closeDialog();
  };

  const onCtaClickHandler = () => {
    promotionStore.sendAnalyticsEvent({ event_action: 'click', click_location: 'cta', button_text: 'Ok' });
    closeDialog();
  };

  const errorCode = options.errorCode as InvalidPromoErrorReason;
  const errorMsg = getInvalidPromoCodeToMsgMap()[errorCode || 'PromoCodeInactiveError'];

  return (
    <DialogBox className={styles.invalidPromoDialog}>
      <div className={styles.header}>
        <button aria-label="Close" className={styles.closeButton} type="button" onClick={onCloseClickHandler}>
          <svg
            aria-hidden="true"
            fill="none"
            height="24"
            viewBox="0 0 24 24"
            width="24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2.5"
            />
          </svg>
        </button>
        <div className={styles.headerContent}>
          <div className={styles.warningIconWrapper}>
            <span className={styles.warningIcon}>!</span>
          </div>
          <h2 className={styles.title}>{t('dialogs.oops')}</h2>
        </div>
      </div>

      <DialogContent className={styles.content}>
        <p className={styles.invalidPromoReason}>{errorMsg || t('promoCode.errors.technicalError')}</p>
        <Button className={styles.okButton} onClick={onCtaClickHandler}>
          {t('common.buttons.okLowerCase')}
        </Button>
      </DialogContent>
    </DialogBox>
  );
});

export default InvalidPromoDialog;
